import apiClient from "@/api/client";
import type {
  AnnualLeaveEntitlement,
  HistoricalLeave,
  LeaveBalanceReportRow,
  LeaveCard,
  LeaveProxyTarget,
  LeaveRequest,
  SickLeave,
} from "../types";

export const leavesApi = {
  list: (params?: Record<string, string>) =>
    apiClient
      .get<{ data: LeaveRequest[] }>("/leave-requests/", { params })
      .then((r) => r.data.data),

  proxyTargets: () =>
    apiClient
      .get<{ data: LeaveProxyTarget[]; meta: { count: number } }>("/leave-requests/proxy_targets/")
      .then((r) => r.data.data),

  get: (id: string) =>
    apiClient
      .get<{ data: LeaveRequest }>(`/leave-requests/${id}/`)
      .then((r) => r.data.data),

  create: (formData: FormData) =>
    apiClient
      .post<{ data: LeaveRequest }>("/leave-requests/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data.data),

  approve: (id: string, comment?: string) =>
    apiClient
      .post<{ data: LeaveRequest }>(`/leave-requests/${id}/approve/`, { comment: comment ?? "" })
      .then((r) => r.data.data),

  reject: (id: string, comment: string) =>
    apiClient
      .post<{ data: LeaveRequest }>(`/leave-requests/${id}/reject/`, { comment })
      .then((r) => r.data.data),

  withdraw: (id: string) =>
    apiClient
      .post<{ data: LeaveRequest }>(`/leave-requests/${id}/withdraw/`)
      .then((r) => r.data.data),

  requestCancellation: (id: string, comment?: string) =>
    apiClient
      .post<{ data: LeaveRequest }>(`/leave-requests/${id}/request_cancellation/`, { comment: comment ?? "" })
      .then((r) => r.data.data),

  approveCancellation: (id: string) =>
    apiClient
      .post<{ data: LeaveRequest }>(`/leave-requests/${id}/approve_cancellation/`)
      .then((r) => r.data.data),

  generatePdf: (data: { leave_type: string; other_type?: string; start_date: string; end_date: string; days_count: number; city?: string; is_hourly?: boolean; hours_count?: number; minutes_count?: number; employee?: string }) =>
    apiClient.post("/leave-requests/generate_pdf/", data, { responseType: "blob" }),

  getCompanyHolidays: () =>
    apiClient
      .get<{ data: Array<{ id: string; start_date: string; end_date: string; description: string }> }>(
        "/company-holidays/",
      )
      .then((r) => r.data.data),

  getEntitlement: (year: number, employeeId?: string) =>
    apiClient
      .get<{ data: AnnualLeaveEntitlement }>("/leave-entitlement/", {
        params: { year, ...(employeeId ? { employee: employeeId } : {}) },
      })
      .then((r) => r.data.data),

  patchEntitlement: (data: {
    employee: string;
    year: number;
    days_total?: number;
    prev_year_carryover?: number;
    historical_used_minutes?: number;
    historical_note?: string;
    manual_monthly_minutes?: (number | null)[] | null;
  }) =>
    apiClient
      .patch<{ data: AnnualLeaveEntitlement }>("/leave-entitlement/", data)
      .then((r) => r.data.data),

  getBalanceReport: (year: number) =>
    apiClient
      .get<{ data: { year: number; rows: LeaveBalanceReportRow[] } }>("/leave-balance-report/", {
        params: { year },
      })
      .then((r) => r.data.data),

  exportBalanceReport: (year: number) =>
    apiClient.get("/leave-balance-report/", {
      params: { year, export: "xlsx" },
      responseType: "blob",
    }),

  getLeaveCard: (employeeId: string) =>
    apiClient
      .get<{ data: LeaveCard }>("/leave-card/", { params: { employee: employeeId } })
      .then((r) => r.data.data),

  patchEmploymentStart: (employeeId: string, employmentStartOverride: string | null) =>
    apiClient
      .patch<{ data: { employment_start_override: string | null; employment_start_date: string | null } }>(
        "/leave-card/",
        { employee: employeeId, employment_start_override: employmentStartOverride },
      )
      .then((r) => r.data.data),
};

export const historicalLeavesApi = {
  list: (params?: Record<string, string>) =>
    apiClient
      .get<{ data: HistoricalLeave[] }>("/historical-leaves/", { params })
      .then((r) => r.data.data),

  create: (data: {
    employee: string;
    leave_type: string;
    start_date: string;
    end_date: string;
    days_count: number;
    hours_count?: number | null;
    minutes_count?: number | null;
    source: string;
    note?: string;
  }) =>
    apiClient
      .post<{ data: HistoricalLeave }>("/historical-leaves/", data)
      .then((r) => r.data.data),

  update: (id: string, data: Partial<Omit<HistoricalLeave, "id">>) =>
    apiClient
      .patch<{ data: HistoricalLeave }>(`/historical-leaves/${id}/`, data)
      .then((r) => r.data.data),

  delete: (id: string) => apiClient.delete(`/historical-leaves/${id}/`),
};

export const sickLeavesApi = {
  list: (params?: Record<string, string>) =>
    apiClient
      .get<{ data: SickLeave[]; meta: { count: number } }>("/sick-leaves/", { params })
      .then((r) => r.data),

  get: (id: string) =>
    apiClient
      .get<{ data: SickLeave }>(`/sick-leaves/${id}/`)
      .then((r) => r.data.data),

  /** Employees an L4 verifier may file a sick leave on behalf of. */
  proxyTargets: () =>
    apiClient
      .get<{ data: LeaveProxyTarget[]; meta: { count: number } }>("/sick-leaves/proxy_targets/")
      .then((r) => r.data.data),

  create: (data: {
    employee?: string;
    start_date: string;
    end_date: string;
    note?: string;
    /** Verifiers only: false files the L4 as pending (still to be checked in ZUS). */
    verify_now?: boolean;
    verification_comment?: string;
  }) =>
    apiClient
      .post<{ data: SickLeave }>("/sick-leaves/", data)
      .then((r) => r.data.data),

  verify: (id: string, verification_comment?: string) =>
    apiClient
      .post<{ data: SickLeave }>(`/sick-leaves/${id}/verify/`, { verification_comment: verification_comment ?? "" })
      .then((r) => r.data.data),

  reject: (id: string, verification_comment: string) =>
    apiClient
      .post<{ data: SickLeave }>(`/sick-leaves/${id}/reject/`, { verification_comment })
      .then((r) => r.data.data),

  delete: (id: string) =>
    apiClient.delete(`/sick-leaves/${id}/`),
};
